/* eslint-disable @typescript-eslint/no-misused-promises */
import { useSeekaConverge } from "@seeka-labs/converge-react";
import { Box, PrimaryButton, Stack, Text } from "flicket-ui";
import { useLocalStorage } from "usehooks-ts";
import CustomModal from "~components/CustomModal";
import { WaitlistForm, waitlistFormId } from "~components/events/waitlist";
import { WaitListFormValues } from "~components/events/waitlist/interface";
import { useAccount, useOrganization } from "~hooks";
import { useReCaptchaSDK } from "~hooks/useReCaptcha";
import { useReferralCode } from "~hooks/useReferralCode";
import { handleRegistrationFormSubmit } from "~pages/events/[eventId]/registration";
import { PageContext } from "../hooks/useDetailsPageContext";
import { getRegisterLabel } from "./events.MainCTA";
import { AttributeNames, PageName } from "~telemetry/enums/AttributeNames";
import { useGoogleAnalyticsPageView } from "~hooks/useGoogleAnalyticsPageView";
import usePageLoad from "~telemetry/traces/usePageLoad";
import { useDetailsPageModalState } from "../hooks/useDetailsPageModalState";

export const getRegistrationFormValueLocalStorage = (releaseId: string) => ({
  key: `registration-form-value-${releaseId}`,
  defaultValue: {
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    phoneNumber: undefined,
    usableForMarketing: undefined,
    customFields: [],
  } as WaitListFormValues,
});

export const getRegistrationFormKeyAndValues = (
  releaseId: string
): [string, WaitListFormValues | undefined] | undefined => {
  const { defaultValue, key } = getRegistrationFormValueLocalStorage(releaseId);
  return [
    key,
    JSON.parse(
      localStorage.getItem(key) || JSON.stringify(defaultValue)
    ) as WaitListFormValues,
  ];
};

export const RegistrationModalContent = ({
  pageContext,
}: {
  pageContext: PageContext;
}) => {
  const { setActiveModal } = useDetailsPageModalState(pageContext);
  const { recaptchaSdkGenerator } = useReCaptchaSDK();
  const { referralCode, referralCodeVersion } = useReferralCode();
  const { organization } = useOrganization();
  const { revaildateUser } = useAccount();
  const { event, setUserEmail } = pageContext;
  const converge = useSeekaConverge();

  const { defaultValue, key } = getRegistrationFormValueLocalStorage(
    event.presaleRelease?.id
  );

  const [, setFormValues] = useLocalStorage(key, defaultValue);

  useGoogleAnalyticsPageView(
    !!event?.id,
    PageName.EVENT_WAITLIST_PAGE,
    event?.id
  );

  const activeRegistrationId = (() => {
    if (
      pageContext.state === "registration-presale-competition" ||
      pageContext.state === "registration-competition" ||
      pageContext.state === "onsale-competition" ||
      pageContext.state === "registration-presale-pre-purchase-competition"
    ) {
      return pageContext.competition.id;
    }

    return undefined;
  })();

  usePageLoad(
    PageName.EVENT_WAITLIST_PAGE,
    {
      forwardToNest: true,
      eventId: event?.id,
      attributes: {
        [AttributeNames.FLICKET_COMPETITION_ID]: activeRegistrationId,
      },
    },
    // Only fire the page-load when:
    // - the event is loaded
    // - the active competition is loaded (if there is one)
    // - no redirect will occur due to a) a public release being available or no waitlist available
    !!(event?.id && !event?.hasPublicRelease && event?.isWaitlistAvailable)
  );

  const isPrePurchaseActive = event.presaleRelease?.isPrePurchaseActive;

  let buttonText = "Register";
  let descriptionText =
    "Be the first to hear about tickets for this event. We’ll email you as soon as they’re available.";

  // A presale has been linked
  if (event.presaleRelease) {
    buttonText = "Notify me when presale opens";
    descriptionText =
      "Sign up for early access to presale tickets! We'll let you know the moment they're live.";
  }

  // Pre-authorisation has been setup and is active
  if (event.presaleRelease?.isPrePurchaseActive) {
    buttonText = "Register";
    descriptionText =
      "Sign up for early access to presale tickets! We'll let you know the moment they're live.";
  }

  return (
    <>
      <CustomModal.Header>
        {getRegisterLabel(pageContext, organization.id)}
      </CustomModal.Header>
      <CustomModal.Content>
        <Stack direction={"vertical"} gap={3}>
          {descriptionText && <Text variant="regular">{descriptionText}</Text>}
          <Box width="100%">
            <WaitlistForm
              waitlistType="registration"
              handleSubmit={async (values, actions) => {
                await handleRegistrationFormSubmit(
                  values,
                  actions,
                  {
                    organization,
                    recaptchaSdkGenerator,
                    referralCode,
                    referralCodeVersion,
                    converge,
                    revaildateUser,
                    event,
                    releaseId: event.presaleRelease?.id,
                  },
                  (data) => {
                    setUserEmail(values.email);
                    pageContext.setWaitlistUserId(data?.joinWaitlist?.id);
                    setActiveModal("registration-success");
                    if (isPrePurchaseActive) {
                      setFormValues(values);
                    }
                  }
                );
              }}
              presaleInformation={event?.presaleInformation}
              customFields={event?.customFields?.filter((customField) =>
                customField.targetArea.includes("registration")
              )}
              hideSubmitButton
            />
          </Box>
        </Stack>
      </CustomModal.Content>
      <CustomModal.Footer>
        <PrimaryButton type="submit" form={waitlistFormId}>
          {buttonText}
        </PrimaryButton>
      </CustomModal.Footer>
    </>
  );
};
